import GenericErrorTemplate from "@/components/GenericErrorTemplate";
import { log } from "@/config/logging";
import { pageLayout } from "@/theme/pageLayout";
import { Box } from "@mui/material";
import { Collapse } from "@trimble/modus-cmx";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { isRouteErrorResponse, useRouteError } from "react-router";

type ErrorContentProps = {
  title: string;
  description?: string;
  summary?: string;
  details?: string;
};

const ErrorContent = (props: ErrorContentProps) => {
  const { title, description, summary, details } = props;
  return (
    <Box sx={pageLayout}>
      <GenericErrorTemplate description={description} header={title} />
      {summary && details && (
        <Collapse
          bordered
          expanded
          options={{
            title: summary,
          }}
        >
          <div slot="content">
            <pre>{details}</pre>
          </div>
        </Collapse>
      )}
    </Box>
  );
};

export function ErrorBoundary() {
  const error = useRouteError();
  const { t } = useTranslation();

  useEffect(() => {
    if (error instanceof Error) {
      log.error(error);
    } else if (isRouteErrorResponse(error)) {
      log.error(
        new Error(`HTTP ${String(error.status)}: ${error.statusText}`),
        { attributes: { status: error.status } }
      );
    } else if (error !== undefined && error !== null) {
      log.error(error, { message: "Unknown route error" });
    }
  }, [error]);

  if (isRouteErrorResponse(error)) {
    const details =
      typeof error.data === "string" ? error.data : JSON.stringify(error.data);
    return (
      <ErrorContent
        details={details}
        summary={t("error.data")}
        title={t("error.http_error_title", {
          status: error.status,
          statusText: error.statusText,
        })}
      />
    );
  } else if (error instanceof Error) {
    return (
      <ErrorContent
        description={error.message}
        details={error.stack}
        summary={t("error.stack_trace")}
        title={t("error.generic_error")}
      />
    );
  }
  return <ErrorContent title={t("error.unknown_error")} />;
}
